import React from 'react';
import InlineMessage from '@atlaskit/inline-message';
import { IconAppearance } from '@atlaskit/inline-message/types';

import { EditContext } from '@/controllers';
import { LinkWithRef } from '@/components/primitive';
import { DAC_PERFPORTAL_SLO_TUTORIAL_LINK } from '@/constants';
import ExternalLinkAction from '@/components/primitive/external-link';

const states = {
  OwnedByOther: {
    messageType: 'error',
    messageTitle: 'Performance SLO already defined',
    message:
      'This Performance Portal Population Split already has a \
      Performance Target defined by another SLO.',
    action: function action({ slo }: OwnedBySloProps) {
      return slo && <LinkWithRef href={`/slo/${slo.publicId}`}>Go to existing SLO</LinkWithRef>;
    },
  },
  OwnedByCurrent: {
    messageType: 'info',
    messageTitle: 'This Population is associated with the current Performance SLO',
    message:
      'This Performance Portal Population Split is currently associated with this SLO. \
      If you are the owner of this SLO, you may modify the Performance Target if you wish.',
  },
  OwnedByNone: {
    messageType: 'confirmation',
    messageTitle: 'No existing Performance SLO defined',
    message:
      'If you are the owner of this Population in Performance Portal, \
      you may wish to define an SLO on this Population Split by adding a Performance Target.',
  },
  Null: null,
} as Record<
  'OwnedByOther' | 'OwnedByCurrent' | 'OwnedByNone' | 'Null',
  {
    messageType: IconAppearance;
    messageTitle: string;
    message: string;
    action?: (args: any) => React.ReactNode;
  } | null
>;

export function getState({
  slo,
  currentSloPublicId,
}: OwnedBySloProps & { currentSloPublicId: string }) {
  if (!slo) {
    return 'OwnedByNone';
  }

  const { publicId: associatedSloPublicId } = slo;

  if (associatedSloPublicId === currentSloPublicId) {
    return 'OwnedByCurrent';
  }

  if (associatedSloPublicId !== currentSloPublicId) {
    return 'OwnedByOther';
  }

  return 'Null';
}

type OwnedBySloProps = { slo: API.UIMetrics.PopulationFieldType['slo'] | null };

const OwnedBySlo: React.FC<OwnedBySloProps> = ({ slo }) => {
  const { publicId: currentSloPublicId } = React.useContext(EditContext);

  const state = states[getState({ slo, currentSloPublicId })];

  if (state === null) return <></>;

  const { messageType, messageTitle, message, action } = state;

  return (
    <InlineMessage type={messageType} title={messageTitle} testId="owned-by-slo">
      <p>
        {message} {action && action({ slo })}
        <br />
        <ExternalLinkAction text="View our guide on DAC." href={DAC_PERFPORTAL_SLO_TUTORIAL_LINK} />
      </p>
    </InlineMessage>
  );
};

export default OwnedBySlo;
